import React, { useMemo, useState, useRef } from "react";
import useApi from "../../CustomHooks/useApi";
import { useToast } from "../../../Contexts/ToastContext";
import { financeAPI } from "../../../api/apiClient";
import "../../../css/Finance/StatementVerificationSection.css";
import Loading from "../../../Loading";

const CARD_FILTERS = {
  ALL_FROM_FILE: "allFromFile",
  MATCHED: "matched",
  UNMATCHED: "unmatched",
  MISSING: "missing",
};

const EXCEL_MODES = {
  BY_VARIANTS: 1,
  COMBINED: 2,
};

const StatementVerificationSection = ({ tripGuid }) => {
  const { callApi, isLoading } = useApi();
  const { showToast } = useToast();

  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [cardFilter, setCardFilter] = useState(CARD_FILTERS.ALL_FROM_FILE);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  });


  const fileInputRef = useRef(null);

  const handleVerify = (file) => {
    if (!file) return;

    setSelectedFile(file);
    setResult(null);

    callApi(
      () => financeAPI.verifyBankStatement(tripGuid, file),
      (response) => {
        setResult(response);
        setCardFilter(CARD_FILTERS.ALL_FROM_FILE);
      }
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("pl-PL");
  };

  const openFilePicker = () => {
    if (isLoading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    handleVerify(file);

    e.target.value = "";
  };

  const formatAmount = (value) =>
    Number(value ?? 0).toLocaleString("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const toggleExpandedRow = (section, key) => {
    const id = `${section}-${key}`;
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isReturn = (item) =>
    Number(item?.matchStatus) === 3 || Number(item?.amount) < 0;

  const isManualMatch = (item) => Number(item?.matchStatus) === 2;

  const matchedManualCount = useMemo(
    () =>
      result?.matchedFromStatement?.filter((x) => Number(x.matchStatus) === 2)
        .length ?? 0,
    [result]
  );

  const missingManualCount = useMemo(
    () =>
      result?.missingFromStatement?.filter((x) => Number(x.matchStatus) === 2)
        .length ?? 0,
    [result]
  );

  const showMatchedSection =
    cardFilter === CARD_FILTERS.ALL_FROM_FILE ||
    cardFilter === CARD_FILTERS.MATCHED;

  const showUnmatchedSection =
    cardFilter === CARD_FILTERS.ALL_FROM_FILE ||
    cardFilter === CARD_FILTERS.UNMATCHED;

  const showMissingSection =
    cardFilter === CARD_FILTERS.MISSING ||
    cardFilter === CARD_FILTERS.ALL_FROM_FILE;

  const getSortableValue = (item, key, section) => {
    switch (key) {
      case "date":
        return section === "missing"
          ? item?.transferDate ?? null
          : item?.bookingDate ?? null;

      case "amount":
        return Number(item?.amount ?? 0);

      case "title":
        return (item?.title ?? "").toString().toLowerCase();

      case "reservationCode":
        return (item?.reservationCode ?? "").toString().toLowerCase();

      case "counterpartyName":
        if (section === "missing") {
          return (item?.senderName ?? "").toString().toLowerCase();
        }
        return (item?.counterpartyName ?? "").toString().toLowerCase();

      case "comment":
        return (item?.comment ?? "").toString().toLowerCase();

      default:
        return item?.[key];
    }
  };

  const sortData = (data, section) => {
    if (!data) return [];

    const sorted = [...data];

    sorted.sort((a, b) => {
      let aValue = getSortableValue(a, sortConfig.key, section);
      let bValue = getSortableValue(b, sortConfig.key, section);

      if (sortConfig.key === "date") {
        const aDate = aValue ? new Date(aValue).getTime() : 0;
        const bDate = bValue ? new Date(bValue).getTime() : 0;

        if (aDate < bDate) return sortConfig.direction === "asc" ? -1 : 1;
        if (aDate > bDate) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const requestSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  return (
    <div className="statement-verification-section">
      <div className="statement-verification-header">
        <div className="statement-verification-header-title">
          Weryfikacja wyciągu bankowego
        </div>

        <div className="statement-upload-box">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          <div
            className={`statement-upload-action ${
              isLoading  ? "disabled" : ""
            }`}
            onClick={openFilePicker}
          >
            <i
              className={`fa-solid ${
                isLoading ? "fa-spinner fa-spin" : "fa-file-import"
              }`}
            />
            <span>{isLoading ? "Wczytywanie..." : "Wczytaj wyciąg CSV"}</span>
          </div>
        </div>

        {selectedFile && (
          <div className="statement-selected-file">
            Wybrany plik: <strong>{selectedFile.name}</strong>
          </div>
        )}
      </div>

      {isLoading ? (
        <Loading text="Wczytywanie wyciągu, może to chwilę potrwać ..." />
      ) : result && (
        <>
          <div className="statement-summary-cards">
            <div
              className={`statement-summary-card clickable ${
                cardFilter === CARD_FILTERS.ALL_FROM_FILE ? "active" : ""
              }`}
              onClick={() => setCardFilter(CARD_FILTERS.ALL_FROM_FILE)}
            >
              <div className="label">Wczytano z pliku</div>
              <div className="value">{result.totalRows ?? 0}</div>
            </div>

            <div
              className={`statement-summary-card success clickable ${
                cardFilter === CARD_FILTERS.MATCHED ? "active" : ""
              }`}
              onClick={() => setCardFilter(CARD_FILTERS.MATCHED)}
            >
              <div className="label">Powiązane</div>
              <div className="value">{result.matchedFromStatement?.length ?? 0}</div>
            </div>

            <div
              className={`statement-summary-card warning clickable ${
                cardFilter === CARD_FILTERS.UNMATCHED ? "active" : ""
              }`}
              onClick={() => setCardFilter(CARD_FILTERS.UNMATCHED)}
            >
              <div className="label">Niepowiązane</div>
              <div className="value">{result.unmatchedFromStatement?.length ?? 0}</div>
            </div>

            <div
              className={`statement-summary-card danger clickable ${
                cardFilter === CARD_FILTERS.MISSING ? "active" : ""
              }`}
              onClick={() => setCardFilter(CARD_FILTERS.MISSING)}
            >
              <div className="label">Brakuje na wyciągu</div>
              <div className="value">{result.missingFromStatement?.length ?? 0}</div>
            </div>
          </div>

          {showMatchedSection && (
            <div className="statement-results-section">
              <h3>Powiązane z wyciągu ({result.matchedFromStatement?.length ?? 0})</h3>

              {showMatchedSection && matchedManualCount > 0 && (
                <div className="statement-manual-info">
                  Uwaga: Niektóre przelewy mają ręczne powiązania z rezerwacjami
                  (Liczba przelewów: {matchedManualCount})
                </div>
              )}

              {(result.matchedFromStatement?.length ?? 0) === 0 ? (
                <div className="statement-empty-box">Brak pozycji.</div>
              ) : (
                <div className="statement-table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th onClick={() => requestSort("date")}>
                          Data{renderSortIndicator("date")}
                        </th>
                        <th onClick={() => requestSort("amount")}>
                          Kwota{renderSortIndicator("amount")}
                        </th>
                        <th onClick={() => requestSort("title")}>
                          Tytuł{renderSortIndicator("title")}
                        </th>
                        <th onClick={() => requestSort("reservationCode")}>
                          Rezerwacja{renderSortIndicator("reservationCode")}
                        </th>
                        <th onClick={() => requestSort("counterpartyName")}>
                          Kontrahent{renderSortIndicator("counterpartyName")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortData(result.matchedFromStatement, "matched").map(
                        (item, index) => {
                          const key = `${item.matchedBankTransferGuid}-${index}`;
                          const expanded = expandedRows[`matched-${key}`];

                          return (
                            <React.Fragment key={key}>
                              <tr
                                className={`statement-row clickable ${
                                  isReturn(item) ? "is-return" : "is-incoming"
                                } ${isManualMatch(item) ? "is-manual-match" : ""}`}
                                onClick={() => toggleExpandedRow("matched", key)}
                              >
                                <td>{formatDate(item.bookingDate)}</td>
                                <td className="amount-col">{formatAmount(item.amount)}</td>
                                <td className="title-col">{item.title || "-"}</td>
                                <td>{item.reservationCode || "-"}</td>
                                <td>{item.counterpartyName || "-"}</td>
                              </tr>

                              {expanded && (
                                <tr className="statement-details-row">
                                  <td colSpan={5}>
                                    <div className="statement-details-grid">
                                      <div>
                                        <strong>Numer rachunku:</strong>{" "}
                                        {item.counterpartyAccountNumber || "-"}
                                      </div>

                                      <div>
                                        <strong>Typ powiązania:</strong>{" "}
                                        {isManualMatch(item) ? "Ręczne" : "Automatyczne"}
                                      </div>

                                      <div>
                                        <strong>Rodzaj:</strong>{" "}
                                        {isReturn(item) ? "Zwrot" : "Wpływ"}
                                      </div>

                                      <div>
                                        <strong>Komentarz:</strong> {item.comment || "-"}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {showUnmatchedSection && (
            <div className="statement-results-section">
              <h3>
                Niepowiązane z wyciągu ({result.unmatchedFromStatement?.length ?? 0})
              </h3>

              {(result.unmatchedFromStatement?.length ?? 0) === 0 ? (
                <div className="statement-empty-box">Brak pozycji.</div>
              ) : (
                <div className="statement-table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th onClick={() => requestSort("date")}>
                          Data{renderSortIndicator("date")}
                        </th>
                        <th onClick={() => requestSort("amount")}>
                          Kwota{renderSortIndicator("amount")}
                        </th>
                        <th onClick={() => requestSort("title")}>
                          Tytuł{renderSortIndicator("title")}
                        </th>
                        <th onClick={() => requestSort("counterpartyName")}>
                          Kontrahent{renderSortIndicator("counterpartyName")}
                        </th>
                        <th onClick={() => requestSort("comment")}>
                          Powód{renderSortIndicator("comment")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortData(result.unmatchedFromStatement, "unmatched").map(
                        (item, index) => {
                          const key = `${index}`;
                          const expanded = expandedRows[`unmatched-${key}`];

                          return (
                            <React.Fragment key={key}>
                              <tr
                                className={`statement-row clickable ${
                                  Number(item.amount) < 0 ? "is-return" : "is-incoming"
                                }`}
                                onClick={() => toggleExpandedRow("unmatched", key)}
                              >
                                <td>{formatDate(item.bookingDate)}</td>
                                <td className="amount-col">{formatAmount(item.amount)}</td>
                                <td className="title-col">{item.title || "-"}</td>
                                <td>{item.counterpartyName || "-"}</td>
                                <td>{item.comment || "-"}</td>
                              </tr>

                              {expanded && (
                                <tr className="statement-details-row">
                                  <td colSpan={5}>
                                    <div className="statement-details-grid">
                                      <div>
                                        <strong>Numer rachunku:</strong>{" "}
                                        {item.counterpartyAccountNumber || "-"}
                                      </div>

                                      <div>
                                        <strong>Rodzaj:</strong>{" "}
                                        {Number(item.amount) < 0 ? "Zwrot" : "Wpływ"}
                                      </div>

                                      <div>
                                        <strong>Pełny komentarz:</strong>{" "}
                                        {item.comment || "-"}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {showMissingSection && (
            <div className="statement-results-section">
              <h3>
                Przelewy w systemie których nie ma na wyciągu (
                {result.missingFromStatement?.length ?? 0})
              </h3>

              {showMissingSection && missingManualCount > 0 && (
                <div className="statement-manual-info">
                  Uwaga: Niektóre z przelewów niebędące na wyciągu mają w systemie
                  ręczne przypięcie (Liczba przelewów: {missingManualCount})
                </div>
              )}

              {(result.missingFromStatement?.length ?? 0) === 0 ? (
                <div className="statement-empty-box">Brak pozycji.</div>
              ) : (
                <div className="statement-table-wrapper">
                  <table className="statement-table">
                    <thead>
                      <tr>
                        <th onClick={() => requestSort("date")}>
                          Data{renderSortIndicator("date")}
                        </th>
                        <th onClick={() => requestSort("amount")}>
                          Kwota{renderSortIndicator("amount")}
                        </th>
                        <th onClick={() => requestSort("title")}>
                          Tytuł{renderSortIndicator("title")}
                        </th>
                        <th onClick={() => requestSort("reservationCode")}>
                          Rezerwacja{renderSortIndicator("reservationCode")}
                        </th>
                        <th onClick={() => requestSort("counterpartyName")}>
                          Kontrahent{renderSortIndicator("counterpartyName")}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {sortData(result.missingFromStatement, "missing").map(
                        (item, index) => {
                          const stripeClass = index % 2 === 0 ? "row-even" : "row-odd";

                          return (
                            <tr
                              key={item.bankTransferGuid}
                              className={`statement-row ${stripeClass} ${
                                isReturn(item) ? "is-return" : "is-incoming"
                              } ${isManualMatch(item) ? "is-manual-match" : ""}`}
                            >
                              <td>{formatDate(item.transferDate)}</td>
                              <td className="amount-col">{formatAmount(item.amount)}</td>
                              <td className="title-col">{item.title || "-"}</td>
                              <td>{item.reservationCode || "-"}</td>
                              <td>{item.senderName || "-"}</td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StatementVerificationSection;